import { bcEnums } from 'common/common/constants/boldchat-enums';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AudioService } from 'common/audio-service/audio.service';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';
import { BehaviorSubject, from, Observable, Subscription } from 'rxjs';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { OperatorStatusService } from 'shared/services/operator-status/operator-status.service';
import { ModalService } from 'shared/components/modal-dialog/modal-dialog.service';
import { BcSettingsService } from 'common/settings/settings.service';
import { bcAuthEvents } from 'common/common/constants/auth-events';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { BcConfigService } from 'common/config/provider';
import { ServiceAvailabilityCollectorService } from 'shared/services/service-availability-collector/service-availability-collector-service';
import { LoggerService } from 'common/logger/logger.service';
import { LogService } from 'common/logger/log.service';
import { ServiceTypeModel } from 'common/common/constants/service-types';
import { CommonAuthService } from 'common/auth/common-auth.service';

@Component({
  selector: 'app-operator-status-buttons',
  templateUrl: './operator-status-buttons.component.html',
  styleUrls: ['./operator-status-buttons.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OperatorStatusButtonsComponent implements OnInit, OnDestroy {
  readonly bcEnums = bcEnums;

  private readonly isLoadingSubject = new BehaviorSubject(true);
  readonly isLoading$ = this.isLoadingSubject.asObservable();

  services$: Observable<ServiceTypeModel[]>;
  isACDModalOpen = false;
  smsDisabled: boolean;

  private readonly _logger: LoggerService;
  private _clientServicesSubscription: Subscription;
  private _notifySetAwaySubscription: Subscription;
  private _logoutSubscription: Subscription;

  constructor(private readonly _bcAnalytics: BcAnalytics,
              private readonly _bcModalService: ModalService,
              private readonly _operatorStatusService: OperatorStatusService,
              private readonly _audioService: AudioService,
              private readonly _logoutSubscriber: LogoutSubscriber,
              private readonly _bcConfig: BcConfigService,
              private readonly _bcSettingsService: BcSettingsService,
              private readonly _rxEmitter: RxEmitter,
              private readonly _changeDetector: ChangeDetectorRef,
              private readonly _serviceCollectorService: ServiceAvailabilityCollectorService,
              private readonly _logService: LogService,
              private readonly _commonAuth: CommonAuthService) {
    this._logger = this._logService.getLoggerInstance(OperatorStatusButtonsComponent.name);
  }

  ngOnInit(): void {
    this.services$ = from(this._serviceCollectorService.getAvailableServicesByServiceTypeValue());
    this.calculateSmsDisabled();

    this._logoutSubscription = this._logoutSubscriber.subscribe(() => this.closeModal());

    const clientServicesStore = this._operatorStatusService.observeClientStatuses();
    this._clientServicesSubscription = clientServicesStore
      .subscribe(obs => {
        obs.forEach(entry => {
          this._operatorStatusService.setServiceStatusMap(entry);
        });
        this.isLoadingSubject.next(false);
        this.checkSmsStatus();
      }, console.error);

    this._notifySetAwaySubscription = this._rxEmitter.subscribe(bcAuthEvents.promptAwayRequired, (message) => {
      this.notifySetAway(message);
    });
  }

  ngOnDestroy(): void {
    if (this._logoutSubscription) {
      this._logoutSubscription.unsubscribe();
      this._logoutSubscription = null;
    }
    if (this._clientServicesSubscription) {
      this._clientServicesSubscription.unsubscribe();
      this._clientServicesSubscription = null;
    }
    if (this._notifySetAwaySubscription) {
      this._notifySetAwaySubscription.unsubscribe();
      this._notifySetAwaySubscription = null;
    }
  }

  private async calculateSmsDisabled(): Promise<void> {
    if (!(await this._commonAuth.isBoldEnabled())) {
      this.smsDisabled = true;
      return;
    }
    this._bcSettingsService.getSettings().then(settings => {
      if (this._bcSettingsService.SmsFeatures.isEnabled(settings, BcFeatures.SM_DISABLED)) {
        this.smsDisabled = true;
        this.checkSmsStatus();
      } else {
        this.smsDisabled = false;
      }
    });
    this._changeDetector.detectChanges();
  }

  private checkSmsStatus(): void {
    if (this._operatorStatusService.getStatusType(this.bcEnums.services.SMS) === this.bcEnums.statuses.AVAILABLE && this.smsDisabled) {
      this.changeStatus(this.bcEnums.services.SMS);
    }
  }

  changeStatus(serviceType): void {
    if (this._operatorStatusService.isLockedStatus(serviceType)) {
      return;
    }

    const statusType = this._operatorStatusService.getStatusType(serviceType);

    if (serviceType === this.bcEnums.services.SMS && this.smsDisabled && statusType === this.bcEnums.statuses.AWAY) {
      const modalOptions = {
        headerText: 'bcOpStatus.smsDisabledTitle',
        messageBody: 'bcOpStatus.smsDisabled',
        confirmButton: {
          text: 'bcOpStatus.ok'
        },
        cancelButton: {
          text: ''
        },
        closeable: true
      };
      this._bcModalService.show(modalOptions);

      return;
    }

    if (statusType === this.bcEnums.statuses.OFFLINE) {
      const modalOptions = {
        headerText: 'bcOpStatus.signOnLimitReachedTitle',
        messageBody: 'bcOpStatus.signOnLimitReachedDescription',
        confirmButton: {
          text: 'bcOpStatus.ok'
        },
        cancelButton: {
          text: ''
        },
        closeable: true
      };
      this._bcModalService.show(modalOptions);

      return;
    }

    if (statusType === this.bcEnums.statuses.AVAILABLE) {
      this._operatorStatusService.changeStatusToAway(serviceType);
    } else if (statusType === this.bcEnums.statuses.AWAY) {
      this._operatorStatusService.changeStatusToAvailable(serviceType);
    } else {
      this._logger.logError('changeStatus(): statusType: {}', [statusType]);
      this._bcAnalytics.eventTrack('ChangeStatusError', { service: this._operatorStatusService.getServiceKey(serviceType), status: statusType });
    }
  }

  private closeModal(): void {
    this.isACDModalOpen = false;
    this._bcModalService.close();
    this._changeDetector.detectChanges();
  }

  notifySetAway(message): void {
    const serviceType = message.serviceType;
    const messageToken = message.messageToken;
    if (serviceType === this.bcEnums.services.SMS && !this._bcConfig.current.features.smsMessages) {
      return;
    }

    this._audioService.playAcdSetAway();

    if (!this.isACDModalOpen) {
      this.isACDModalOpen = true;
      const modalOptions = {
        headerText: 'bcOpStatus.notice',
        messageBody: this.getLogoffByAcdMessage(serviceType, messageToken),
        confirmButton: {
          text: this.getLogoffByAcdButtonText(serviceType),
          clickEvent: () => this.changeStatus(serviceType)
        },
        cancelButton: {
          text: 'bcOpStatus.close'
        },
        resultFunction: () => this.modalResult(),
        closeable: true
      };
      this._bcModalService.show(modalOptions);
      this._changeDetector.detectChanges();
    }
  }

  private modalResult(): void {
    this.isACDModalOpen = false;
    this._changeDetector.detectChanges();
  }

  private getLogoffByAcdMessage(serviceType, messageToken?): string {
    if (messageToken && messageToken.token === 'acd#changedstatustoaway#discussion') {
      return 'bcOpStatus.logoff.byAcd.discussions';
    }
    return `bcOpStatus.logoff.byAcd.${this._operatorStatusService.getServiceKey(serviceType)}`;
  }

  private getLogoffByAcdButtonText(serviceType): string {
    return `bcOpStatus.setAvailable.${this._operatorStatusService.getServiceKey(serviceType)}`;
  }
}
